import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Analytics = ({ token }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dateFrom, setDateFrom] = useState(() => {
        const d = new Date();
        d.setDate(1); // Default to start of month
        return d.toISOString().split('T')[0];
    });
    const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);

    const fetchAnalytics = React.useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`https://menu.searchisend.in/apimenu/v2/v2_analytics.php?action=fetch&from=${dateFrom}&to=${dateTo}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const json = await res.json();
            if (json.success) setData(json);
        } catch (err) {
            console.error("Analytics fetch error:", err);
        } finally {
            setLoading(false);
        }
    }, [token, dateFrom, dateTo]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    if (loading) return <div className="admin-card">Loading analytics...</div>;
    if (!data) return <div className="admin-card">Error loading analytics.</div>;

    const { summary, daily_views, device_stats, platform_stats } = data;

    // Helper for simple line chart
    const maxDaily = Math.max(...(daily_views || []).map(v => v.count), 1);
    const vW = 800, vH = 220, pX = 40, pY = 40;
    const getX = (i) => pX + (i * ((vW - 2 * pX) / Math.max(daily_views.length - 1, 1)));
    const getY = (v) => vH - pY - ((v / maxDaily) * (vH - 2 * pY));
    const linePoints = daily_views.map((d, i) => `${getX(i)},${getY(d.count)}`).join(' ');
    const areaPoints = `${getX(0)},${vH - pY} ${linePoints} ${getX(daily_views.length - 1)},${vH - pY}`;
    
    return (
        <div className="admin-card">
            <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
    <i className="fas fa-arrow-left"></i> Back to Dashboard
</Link>
            
            <h1 style={{ marginTop: 0 }}>Menu & Sales Analytics</h1>
            <p style={{ color: 'var(--text-color-secondary)', marginBottom: '15px' }}>Track your restaurant performance, views, and revenue in one place.</p>

            {/* Date Filters */}
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center', background: '#f8fafc', padding: '15px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '25px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label style={{ fontSize: '0.9em', fontWeight: '600', color: '#64748b' }}>From:</label>
                    <input 
                        type="date" 
                        value={dateFrom} 
                        onChange={e => setDateFrom(e.target.value)}
                        style={{ padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label style={{ fontSize: '0.9em', fontWeight: '600', color: '#64748b' }}>To:</label>
                    <input 
                        type="date" 
                        value={dateTo} 
                        onChange={e => setDateTo(e.target.value)}
                        style={{ padding: '8px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                </div>
                <button 
                    onClick={fetchAnalytics}
                    style={{ padding: '8px 20px', background: 'var(--primary-orange)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer' }}
                >
                    Refresh
                </button>
            </div>

            {/* Sales Stats Overview */}
            {data.sales && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                    <div style={{ background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)', padding: '24px', borderRadius: '12px', border: '1px solid #a7f3d0', textAlign: 'center' }}>
                        <span style={{ fontSize: '0.8em', color: '#065f46', fontWeight: '700', display: 'block', marginBottom: '8px' }}>TODAY'S REVENUE</span>
                        <span style={{ fontSize: '2.1rem', fontWeight: '800', color: '#047857' }}>₹{data.sales.today_revenue}</span>
                    </div>
                    <div style={{ background: 'linear-gradient(135deg, #fffbeb, #fef3c7)', padding: '24px', borderRadius: '12px', border: '1px solid #fde68a', textAlign: 'center' }}>
                        <span style={{ fontSize: '0.8em', color: '#92400e', fontWeight: '700', display: 'block', marginBottom: '8px' }}>PERIOD REVENUE</span>
                        <span style={{ fontSize: '2.1rem', fontWeight: '800', color: '#b45309' }}>₹{data.sales.period_revenue}</span>
                    </div>
                    <div style={{ background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', padding: '24px', borderRadius: '12px', border: '1px solid #bfdbfe', textAlign: 'center' }}>
                        <span style={{ fontSize: '0.8em', color: '#1e40af', fontWeight: '700', display: 'block', marginBottom: '8px' }}>TOTAL REVENUE</span>
                        <span style={{ fontSize: '2.1rem', fontWeight: '800', color: '#1d4ed8' }}>₹{data.sales.total_revenue}</span>
                    </div>
                </div>
            )}

            {/* View Stats Overview */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '40px' }}>
                <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <span style={{ fontSize: '0.9em', color: '#64748b', fontWeight: '600', display: 'block', marginBottom: '8px' }}>TOTAL VIEWS</span>
                    <span style={{ fontSize: '2rem', fontWeight: '800', color: 'var(--primary-orange)' }}>{summary.total_views}</span>
                </div>
                <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <span style={{ fontSize: '0.9em', color: '#64748b', fontWeight: '600', display: 'block', marginBottom: '8px' }}>TODAY'S VIEWS</span>
                    <span style={{ fontSize: '2rem', fontWeight: '800', color: 'var(--primary-orange)' }}>{summary.today_views}</span>
                </div>
                <div style={{ background: '#f8fafc', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                    <span style={{ fontSize: '0.9em', color: '#64748b', fontWeight: '600', display: 'block', marginBottom: '8px' }}>PERIOD VIEWS</span>
                    <span style={{ fontSize: '2rem', fontWeight: '800', color: 'var(--primary-orange)' }}>{summary.period_views}</span>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '30px' }}>
                
                {/* Daily Views Line Chart */}
                <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '1.1em' }}>Daily Reach (Selected Period)</h3>
                    
                    {daily_views.length === 0 ? (
                        <div style={{ width: '100%', textAlign: 'center', color: '#94a3b8', padding: '60px 0' }}>No view data yet</div>
                    ) : (
                        <div style={{ position: 'relative', width: '100%', height: '220px' }}>
                            <svg viewBox={`0 0 ${vW} ${vH}`} preserveAspectRatio="none" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
                                <defs>
                                    <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="var(--primary-orange)" stopOpacity="0.3" />
                                        <stop offset="100%" stopColor="var(--primary-orange)" stopOpacity="0.0" />
                                    </linearGradient>
                                </defs>
                                
                                {/* Background Grid Lines */}
                                <line x1={pX} y1={pY} x2={vW - pX} y2={pY} stroke="#f1f5f9" strokeWidth="1" strokeDasharray="5,5" />
                                <line x1={pX} y1={vH/2} x2={vW - pX} y2={vH/2} stroke="#f1f5f9" strokeWidth="1" strokeDasharray="5,5" />
                                <line x1={pX} y1={vH - pY} x2={vW - pX} y2={vH - pY} stroke="#e2e8f0" strokeWidth="1.5" />
                                
                                {/* Area Fill */}
                                {daily_views.length > 1 && (
                                    <polygon points={areaPoints} fill="url(#chartGradient)" />
                                )}
                                
                                {/* Line */}
                                <polyline 
                                    fill="none" 
                                    stroke="var(--primary-orange)" 
                                    strokeWidth="3" 
                                    strokeLinejoin="round"
                                    strokeLinecap="round"
                                    points={linePoints} 
                                />
                                
                                {/* Data Points */}
                                {daily_views.map((day, i) => (
                                    <g key={i} className="chart-point">
                                        <circle 
                                            cx={getX(i)} 
                                            cy={getY(day.count)} 
                                            r="4" 
                                            fill="#fff" 
                                            stroke="var(--primary-orange)" 
                                            strokeWidth="2" 
                                        />
                                    </g>
                                ))}
                            </svg>
                            {/* X-axis labels overlay */}
                            <div style={{ position: 'absolute', bottom: '0', left: 0, width: '100%', display: 'flex', justifyContent: 'space-between', padding: `0 ${pX}px`, boxSizing: 'border-box' }}>
                                {daily_views.map((day, i) => {
                                    const step = Math.ceil(daily_views.length / 6);
                                    if (daily_views.length > 10 && i % step !== 0 && i !== daily_views.length - 1) return <div key={i} style={{ width: 0 }}></div>;
                                    return (
                                        <div key={i} style={{ width: 0, display: 'flex', justifyContent: 'center', overflow: 'visible' }}>
                                            <span style={{ fontSize: '0.7em', color: '#64748b', fontWeight: '500', transform: 'rotate(-45deg)', whiteSpace: 'nowrap', marginTop: '10px' }}>
                                                {new Date(day.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Sales Breakdown */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    
                    {/* Top Items */}
                    <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '1.1em' }}><i className="fas fa-trophy" style={{ color: '#f59e0b', marginRight: '8px' }} />Top Selling Items</h3>
                        {(!data.sales || data.sales.top_items.length === 0) ? <p style={{ color: '#94a3b8' }}>No sales data yet</p> : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {data.sales.top_items.map((item, idx) => (
                                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: idx < data.sales.top_items.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <span style={{ fontWeight: '800', color: '#94a3b8', fontSize: '0.9em', width: '20px' }}>#{idx+1}</span>
                                            <div>
                                                <span style={{ fontWeight: '600', display: 'block', fontSize: '0.95em' }}>{item.item_name}</span>
                                                <span style={{ fontSize: '0.8em', color: '#64748b' }}>{item.total_qty} sold</span>
                                            </div>
                                        </div>
                                        <span style={{ fontWeight: '700', color: '#059669' }}>₹{item.total_revenue}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Payment Modes */}
                    <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '1.1em' }}>Payment Distribution</h3>
                        {(!data.sales || data.sales.payment_modes.length === 0) ? <p style={{ color: '#94a3b8' }}>No payments recorded</p> : (
                            <div style={{ display: 'flex', gap: '10px' }}>
                                {data.sales.payment_modes.map(pm => (
                                    <div key={pm.payment_mode} style={{ flex: 1, padding: '12px', borderRadius: '8px', background: '#f8fafc', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                                        <span style={{ fontSize: '0.75em', fontWeight: '700', color: '#64748b', display: 'block', marginBottom: '4px' }}>{pm.payment_mode.toUpperCase()}</span>
                                        <span style={{ fontWeight: '700', fontSize: '1.1em' }}>₹{pm.amount}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Device Distribution */}
                <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '1.1em' }}>Device Distribution</h3>
                    {device_stats.length === 0 ? <p style={{ color: '#94a3b8' }}>No data</p> : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {device_stats.map(stat => (
                                <div key={stat.device}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.9em' }}>
                                        <span>{stat.device}</span>
                                        <span style={{ fontWeight: '600' }}>{((stat.count / Math.max(summary.period_views, 1)) * 100).toFixed(0)}%</span>
                                    </div>
                                    <div style={{ width: '100%', height: '8px', background: '#f1f5f9', borderRadius: '10px', overflow: 'hidden' }}>
                                        <div style={{ width: `${(stat.count / Math.max(summary.period_views, 1)) * 100}%`, height: '100%', background: '#6366f1' }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Platform Breakdown */}
                <div style={{ background: '#fff', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                    <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '1.1em' }}>Top Platforms</h3>
                    {platform_stats.length === 0 ? <p style={{ color: '#94a3b8' }}>No data</p> : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                            {platform_stats.map(stat => (
                                <div key={stat.platform} style={{ background: '#f8fafc', padding: '10px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', flex: '1 1 120px' }}>
                                    <span style={{ fontSize: '0.8em', color: '#64748b', display: 'block' }}>{stat.platform}</span>
                                    <span style={{ fontWeight: '700', fontSize: '1.1em' }}>{stat.count} <span style={{ fontSize: '0.7em', fontWeight: '400', color: '#94a3b8' }}>views</span></span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};

export default Analytics;
